import React, { useState, useRef, useEffect } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { TweenMax } from "gsap"
import { ANIMATION } from "utils/constants"
import {
  Button,
  Hotel,
  Panelist,
  Program,
  Signup,
  Speaker,
  Success,
} from "components"
import styles from "./contentSection.module.scss"

const ContentSection = ({ content, getRandomPosition, index, position }) => {
  const [success, setSuccess] = useState(false)
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const contentRef = useRef()
  const modalRef = useRef()
  const speakersRef = useRef()
  const programRef = useRef()

  const data = useStaticQuery(graphql`
    query speakersQuery {
      allFile(filter: { relativeDirectory: { eq: "speakers" } }) {
        edges {
          node {
            name
            childImageSharp {
              fluid {
                ...GatsbyImageSharpFluid_noBase64
              }
            }
          }
        }
      }
    }
  `)

  const setShowSuccess = toggle => {
    setSuccess(toggle)
  }

  useEffect(() => {
    const contentTag = contentRef.current
    const modalTag = modalRef.current
    const speakersTag = speakersRef.current
    const programTag = programRef.current

    if (modalIsOpen) {
      TweenMax.to(modalTag, ANIMATION.DURATION, {
        autoAlpha: 1,
        ease: ANIMATION.EASE,
        zIndex: 1,
      })

      TweenMax.fromTo(
        [contentTag, speakersTag, programTag],
        ANIMATION.DURATION,
        {
          y: 10,
        },
        { y: 0, ease: ANIMATION.EASE }
      )
    } else {
      TweenMax.to(modalTag, ANIMATION.DURATION, {
        autoAlpha: 0,
        ease: ANIMATION.EASE,
        zIndex: 0,
      })
    }
  }, [modalIsOpen])

  useEffect(() => {
    TweenMax.to(modalRef.current, 0, {
      autoAlpha: 0,
      zIndex: -1,
    })
  }, [])

  const positionStyle = {
    gridArea: position,
  }

  return (
    <section style={positionStyle}>
      {content.label === "Signup" && <Success success={success} />}
      <Button
        content={content.label}
        modalIsOpen={modalIsOpen}
        setModalIsOpen={setModalIsOpen}
        index={index}
        url={content.url}
        video={content.cover}
      />
      <div ref={modalRef} className={styles.modal}>
        <button
          className={styles.buttonClose}
          onClick={() => {
            getRandomPosition()
            setModalIsOpen(false)
          }}
        >
          <span className={styles.buttonText}>Close</span>
        </button>
        <div ref={contentRef} className={styles.modalContent}>
          <h2 className={styles.title}>{content.title}</h2>
          <div className={styles.bodyContainer}>
            <div className={styles.body}>{content.body}</div>
            {content.info && (
              <div className={styles.info}>
                <span className={styles.infoBlock}>
                  When: {content.info.when}
                </span>
                <span className={styles.infoBlock}>
                  Where:{" "}
                  <a
                    href={content.info.where}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Gund Hall, Harvard GSD
                  </a>
                </span>
                <span className={styles.infoBlock}>
                  Questions?{" "}
                  <a
                    href={`mailto:${content.info.questions}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Send us an email
                  </a>
                </span>
              </div>
            )}

            {content.label === "Signup" && (
              <Signup
                setShowSuccess={setShowSuccess}
                setModalIsOpen={setModalIsOpen}
              />
            )}
          </div>
        </div>

        {content.label === "Speakers" && (
          <div ref={speakersRef} className={styles.speakers}>
            {content.speakers.map((speaker, i) => (
              <Speaker
                key={i}
                content={speaker}
                img={data.allFile.edges[i].node}
              />
            ))}
            {content.panelists && (
              <div className={styles.panelists}>
                <h2 className={styles.title}>Panelists</h2>
                <div className={styles.panelistList}>
                  {content.panelists.map((panelist, i) => {
                    return <Panelist key={i} content={panelist} />
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {content.label === "Program" && (
          <div ref={programRef} className={styles.program}>
            {content.program.map((program, i) => (
              <Program key={i} content={program} />
            ))}
          </div>
        )}

        {content.label === "Stay" && (
          <div className={styles.stay}>
            <div className={styles.stayList}>
              {content.hotels.map((hotel, i) => (
                <Hotel key={i} content={hotel} />
              ))}
            </div>
            <div className={styles.stayIntro}>
              {content.additionalHotels.intro}
            </div>
            <div className={styles.stayList}>
              {content.additionalHotels.hotels.map((hotel, i) => (
                <Hotel key={i} content={hotel} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default ContentSection
